import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import SettingMenu from './settingMenu';
interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  organization: string;
  phoneNumber: string;
  address: string;
  state: string;
  zipCode: string;
  country: string;
  language: string;
  timezone: string;
  currency: string;
}

const ProfileComponent: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profileImage, setProfileImage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ProfileData>({
    defaultValues: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      organization: 'Pixinvent',
      phoneNumber: '202 555 0111',
      address: '',
      state: 'California',
      zipCode: '231465',
      country: '',
      language: '',
      timezone: '',
      currency: ''
    }
  });

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 800 * 1024) {
        alert('File size should not exceed 800KB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleReset = () => {
    setProfileImage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = (data: ProfileData) => {
    console.log('Profile data:', data);
  };

  const handleCancel = () => {
    reset();
    setProfileImage('');
  };

  return (
    <>
      <div className="tab-pane fade show active" id="tabs-profile" role="tabpanel">
        <div className="bg-white rounded-md shadow-lg p-6  mx-auto">
          {/* Profile Image Section */}
          <div className="flex items-center gap-4 mb-8">
            <div className="relative">
              <img
                src="../../assets/img/user-png.png"
                alt="Profile"
                className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={handleUploadClick}
                  type="button"
                  className="px-6 btn-primary w-40 mr-2 ml-0 mt-0">
                  Upload photo
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium">
                  Reset
                </button>
              </div>
              <p className="text-sm text-gray-500">Allowed JPG, GIF or PNG. Max size of 800K</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Form Section */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:gap-6 lg:gap-4 md:gap-4 gap-2">
              {/* First Name */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  className="input-style"
                  {...register('firstName', {
                    required: true,
                    maxLength: 80,
                    pattern: /^[A-Za-z]/
                  })}
                />
                {errors.firstName?.type === 'required' && (
                  <p className="text-red-500 text-sm mt-1">Required Field</p>
                )}
                {errors.firstName?.type === 'maxLength' && (
                  <p className="text-red-500 text-sm mt-1">Max. length reached</p>
                )}
                {errors.firstName?.type === 'pattern' && (
                  <p className="text-red-500 text-sm mt-1">Only alphabets are allowed</p>
                )}
              </div>

              {/* Last Name */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  className="input-style"
                  {...register('lastName', {
                    required: true,
                    maxLength: 80,
                    pattern: /^[A-Za-z]/
                  })}
                />
                {errors.lastName?.type === 'required' && (
                  <p className="text-red-500 text-sm mt-1">Required Field</p>
                )}
                {errors.lastName?.type === 'maxLength' && (
                  <p className="text-red-500 text-sm mt-1">Max. length reached</p>
                )}
                {errors.lastName?.type === 'pattern' && (
                  <p className="text-red-500 text-sm mt-1">Only alphabets are allowed</p>
                )}
              </div>

              {/* Email */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">E-mail</label>
                <input
                  type="email"
                  className="input-style"
                  {...register('email', {
                    required: true,
                    maxLength: 80,
                    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                  })}
                />
                {errors.email?.type === 'required' && (
                  <p className="text-red-500 text-sm mt-1">Required Field</p>
                )}
                {errors.email?.type === 'maxLength' && (
                  <p className="text-red-500 text-sm mt-1">Max. length reached</p>
                )}
                {errors.email?.type === 'pattern' && (
                  <p className="text-red-500 text-sm mt-1">Only email are allowed</p>
                )}
              </div>

              {/* Organization */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">Organization</label>
                <input type="text" className="input-style" {...register('organization')} />
              </div>

              {/* Phone Number */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">Phone Number</label>
                <div className="flex">
                  <select className="input-style rounded-r-none border-r-0 w-24 bg-gray-50">
                    <option>US (+1)</option>
                    <option>UK (+44)</option>
                    <option>IN (+91)</option>
                  </select>
                  <input
                    type="text"
                    className="input-style rounded-l-none flex-1"
                    placeholder="202 555 0111"
                    {...register('phoneNumber', {
                      maxLength: 15
                    })}
                  />
                </div>
                {errors.phoneNumber?.type === 'maxLength' && (
                  <p className="text-red-500 text-sm mt-1">Max. length reached</p>
                )}
              </div>

              {/* Address */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  className="input-style"
                  placeholder="Address"
                  {...register('address')}
                />
              </div>

              {/* State */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">State</label>
                <input type="text" className="input-style" {...register('state')} />
              </div>

              {/* Zip Code */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">Zip Code</label>
                <input type="text" className="input-style" {...register('zipCode')} />
              </div>

              {/* Country */}
              <div className="form-group mb-4">
                <label className="block mb-1 font-medium text-gray-700">Country</label>
                <select className="input-style" {...register('country')}>
                  <option value="">Select</option>
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="IN">India</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            {/* <div className="flex justify-end gap-4 mt-8 pt-4 border-t">
          <button type="button" onClick={handleSubmit(onSubmit)} className="px-6 btn-primary w-32">
            Save changes
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors w-24">
            Cancel
          </button>
          <button
            type="button"
            className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors w-24">
            Buy Now
          </button>
        </div> */}
            <div className=" flex flex-shrink-0 flex-wrap items-center  rounded-b-md">
              <button type="submit" className="px-6 btn-primary w-28 mr-2 ml-0 mt-0">
                Save
              </button>
              <button
                type="button"
                className="mx-0 px-6 btn-primary discard-btn w-28 mt-0"
                data-bs-dismiss="modal">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileComponent;
